import { useRef } from "react";
import classNames from "classnames";
import { useInView } from "framer-motion";
import { graphql } from "gatsby";
import parse from "html-react-parser";

import ItemList from "@core/ui/molecules/item-list/ItemList";
import useVisibilityTracker from "@utils/hooks/useVisibilityTracker";

import { HeadingLevel } from "@atoms/heading/Heading";
import Ksp from "@molecules/ksp/Ksp";
import SectionTitle from "@molecules/section-title/SectionTitle";

import * as styles from "@organisms/ksp-list/KspList.module.scss";

interface KspElement {
  description?: string;
  id?: string;
  imageAlt?: string;
  image?: {
    file: {
      url: string;
    };
  };
  title: string;
  items?: string[];
  itemList?: {
    title?: string;
    items: string[];
  }[];
}

interface KspListProps {
  data: {
    title?: string;
    ksps?: KspElement[];
  };
  variantName?: string;
  titleHeadingLevel?: HeadingLevel;
  adoptionClassName?: string;
}

const KspList = ({
  data: { title, ksps },
  variantName,
  titleHeadingLevel,
  adoptionClassName,
}: KspListProps): JSX.Element => {
  const componentName = "or-ksp-list";
  const { ref } = useVisibilityTracker(variantName, {
    amount: 0.5,
  });
  const ulRef = useRef(null);
  const isUlVisible = useInView(ulRef, {
    amount: 0.15,
    once: true,
  });

  const itemListHeadingLevel = titleHeadingLevel ? titleHeadingLevel + 1 : 3;

  return (
    <section
      ref={ref}
      className={classNames(
        variantName ? `${componentName} ${componentName}--${variantName}` : componentName,
        styles.kspList,
        adoptionClassName,
        {
          [styles[variantName as string]]: variantName,
        }
      )}
    >
      {title && (
        <SectionTitle
          title={parse(title)}
          headingLevel={titleHeadingLevel}
          adoptionClassName={styles.sectionTitle}
        />
      )}
      <ul ref={ulRef}>
        {ksps &&
          ksps.map((ksp, index) => {
            return (
              <li
                key={ksp.id || index}
                className={classNames(styles.kspItemNotVisited, {
                  [styles.kspItemAnimation]: isUlVisible,
                })}
              >
                <Ksp
                  title={ksp.title}
                  imageSrc={ksp.image?.file?.url}
                  imageAlt={ksp.imageAlt || ""}
                >
                  {ksp.description && (
                    <p className={classNames(styles.description, "ksp-description")}>
                      {ksp.description}
                    </p>
                  )}
                  {ksp.items && (
                    <ul className={classNames(styles.items)}>
                      {ksp.items.map((item, index) => {
                        return <li key={index}>{item}</li>;
                      })}
                    </ul>
                  )}
                  {ksp.itemList && (
                    <div className={styles.itemList}>
                      {ksp.itemList.map((i, index) => (
                        <ItemList
                          key={index}
                          items={i.items}
                          title={i.title}
                          titleLevel={itemListHeadingLevel}
                        />
                      ))}
                    </div>
                  )}
                </Ksp>
              </li>
            );
          })}
      </ul>
    </section>
  );
};

export const query = graphql`
  fragment KspFragment on ContentfulKspList {
    title
    ksps {
      id
      description
      title
      items
      image {
        file {
          url
        }
      }
      itemList {
        title
        items
      }
    }
  }
`;

export default KspList;
